<template>
  <div>
    <!-- Add or Edit Product -->
    <productTradesForm
      :dialog="productTradeFormDialog"
      :contractId="contract.id"
      :previsional="previsional"
      :productTradeToEdit="productTradeToEdit"
      @newProduct="newProductAdded"
      @updatedProductTrade="updatedProductTrade"
      @close="productTradeFormDialog = false"
       
    />

    <!-- Toolbar -->
    <v-toolbar card dense height="32">
      <v-icon small pa-0>shopping_cart</v-icon>
      <v-toolbar-title style="margin-left: 8px">
        <h6>Products to trade</h6>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn small depressed round flat @click="addNewProduct()">
        <v-icon small class="pr-2">add</v-icon>
        Add a product
      </v-btn>
    </v-toolbar>

    <v-data-table
      :headers="headers"
      :items="tradedProducts"
      hide-actions
      :pagination="pagination"
      class="productTrades"
      ref="sortableTable"
     
    >
      <template slot="headerCell" slot-scope="props">
        <span
          v-html="
            $vuetify.breakpoint.lgAndDown
              ? props.header[props.header.short ? 'short' : 'text'].replaceAll(
                  ' ',
                  '<br />'
                )
              : props.header.text
          "
        ></span>
      </template>

      <template v-slot:items="props">
        <tr
          class="sortableRow"
          style="height: 36px"
          @click="editProductTrade(props.item)"
        >
          <td><v-icon class="sortHandle"> mdi-drag-horizontal </v-icon></td>
          <td class="caption">{{ props.item.product.name }}</td>
          <td class="caption">
            {{ props.item.packaging ? props.item.packaging.name : "" }}
          </td>
          <td class="caption">
            {{
              props.item.packaging_info ? props.item.packaging_info.name : ""
            }}
          </td>
          <td class="caption font-weight-bold">
            {{ getNetWeight(props.item) }}mt
          </td>
          <td class="caption">
            {{ inSupplierCurrency(props.item.buying_tonne_price) }}
          </td>
          <td class="caption font-weight-bold">
            {{ inSupplierCurrency(getTotalBuyingPrice(props.item)) }}
          </td>
          <td class="caption">
            {{ inCustomerCurrency(props.item.selling_tonne_price) }}
          </td>
          <td class="caption font-weight-bold">
            {{ inCustomerCurrency(getTotalSellingPrice(props.item)) }}
          </td>
          <td class="text-xs-right">
            <destroyModale
              color="error"
              slot="activator"
              resourceName="product_trade"
              :collection="tradedProducts"
              :itemId="props.item.id"
              :warningText="
                'Remove <b>' +
                props.item.product.name.replaceAll('.', '') +
                '</b> from traded products?'
              "
              small
              icon
              icon_name="mdi-close"
              small_icon
              :fab="false"
              px-0
            />
          </td>
        </tr>
      </template>

      <template slot="no-data">
        <div class="text-xs-center">
          <v-btn flat color=" primary" depressed @click="addNewProduct()">
            <v-icon class="pr-2" small>add_circle</v-icon>
            Add a product to visualize score.
          </v-btn>
        </div>
      </template>

      <template slot="footer" class="pt-2">
        <!-- Total BEFORE Xchange rates -->
        <tr
          v-if="displayBuyingExchangeRate || displaySellingExchangeRate">
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td class="font-weight-bold text-xs-right caption">Sub Total:</td>
          <td class="caption font-weight-bold">
            {{ inSupplierCurrency(totalBuyingPrice(previsional)) }}
          </td>
          <td></td>
          <td class="caption font-weight-bold">
            {{ inCustomerCurrency(totalSellingPrice(previsional)) }}
          </td>
          <td></td>
        </tr>

        <!-- Exchange rate row -->
        <tr
          v-if="displayBuyingExchangeRate || displaySellingExchangeRate"
          style="height: 28px"
        >
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td class="caption font-weight-bold text-xs-right">
            {{ $vuetify.breakpoint.lgAndDown ? "XR" : "Exchange rates" }}:
          </td>
          <td>
            <v-text-field
              class="reducedInput"
              prefix="x"
              v-model.number="contract.buying_change_rate"
              @change="updateChangeRates"
              hide-details
              solo
              type="number"
              flat
              dense
              background-color="grey lighten-4"
            />
          </td>
          <td></td>
          <td>
            <v-text-field
              class="reducedInput"
              prefix="x"
              v-model.number="contract.selling_change_rate"
              @change="updateChangeRates"
              hide-details
              solo
              type="number"
              flat
              dense
              background-color="grey lighten-4"
            />
          </td>
          <td></td>
        </tr>

        <!-- Total AFTER Xchange rates -->
        <tr>
          <td></td>
          <td></td>
          <td></td>
          <td class="font-weight-bold text-xs-right">Total:</td>
          <td class="font-weight-bold">{{ totalNetWeightInMT(previsional).toFixed(3) }}mt</td>
          <td class="font-weight-bold text-xs-right">Total:</td>
          <td class="font-weight-bold">
            {{ inScoreCurrency(totalBuyingPriceScoreCurrency(previsional)) }}
          </td>
          <td></td>
          <td class="font-weight-bold">
            {{ inScoreCurrency(totalSellingPriceScoreCurrency(previsional)) }}
          </td>
          <td></td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import productTradesForm from "./productTradesForm";
import Sortable from "sortablejs";
import scoreCost from "../../mixins/scoreCost";
import REORDER_PRODUCT_TRADES from "../../graphql/product_trades/reorder.gql"
import UPDATE_CONTRACT from "../../graphql/contracts/update.gql"

export default {
  name: "productTrades",
  mixins: [scoreCost],
  components: { productTradesForm, Sortable },
  props: {
    previsional: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      productTradeToEdit: null,
      productTradeFormDialog: false,

      pagination: {
        descending: false,
        rowsPerPage: -1,
        sortBy: "order_index",
      },
    };
  },
  computed: {
    ...mapGetters("contractStore", [
      "contract",
      "totalSellingPrice",
      "totalNetWeightInMT",
      "totalSellingPriceScoreCurrency",
      "totalBuyingPrice",
      "totalBuyingPriceScoreCurrency",
    ]),
    ...mapGetters("commonStore", [
      "product_categories",
      "products",
      "packagings",
      "packaging_infos",
      "categoryProducts",
      "getNameById",
    ]),
    tradedProducts() {
      return this.previsional
        ? this.contract.product_trades
        : this.contract.real_product_trades;
    },
    headers() {
      return [
        {
          text: "",
          sortable: false,
          value: "order_index",
        },
        {
          text: "Product",
          sortable: false,
          value: "product.name",
        },
        {
          text: "Packaging",
          sortable: false,
          value: "packaging.name",
        },
        {
          text: "Packaging Info",
          sortable: false,
          value: "packaging_info",
        },
        {
          text: "Net Weight (mt)",
          sortable: false,
          value: "net_weight",
          short: "NW (MT)",
        },
        {
          text:
            "Buying Price (" + this.contract.supplier_currency.code + "/MT)",
          sortable: false,
          value: "buying_tonne_price",
          short: "BP (" + this.contract.supplier_currency.code + "/MT)",
        },
        {
          text:
            "Total buying price (" + this.contract.supplier_currency.code + ")",
          sortable: false,
          value: "total_buying_price",
          short: "TBP (" + this.contract.supplier_currency.code + ")",
        },
        {
          text:
            "Selling Price (" +
            this.contract.customer_currency.code +
            "/MT" +
            ")",
          short: "SP (" + this.contract.customer_currency.code + "/MT" + ")",
          sortable: false,
          value: "selling_tonne_price",
        },
        {
          text:
            "Total selling price (" +
            this.contract.customer_currency.code +
            ")",
          sortable: false,
          value: "total_selling_price",
          short: "TSP (" + this.contract.customer_currency.code + ")",
        },
        {
          text: "Actions",
          align: "right",
          sortable: false,
        },
      ];
    },
    displayBuyingExchangeRate() {
      
      return (
        this.contract.supplier_currency.code !=
        this.contract.score_currency.code
      );
    },
    displaySellingExchangeRate() {
     
      return (
        this.contract.customer_currency.code !=
        this.contract.score_currency.code
      );
    },
  },
  methods: {
    updatedProductTrade(event) {
      let index = this.tradedProducts.findIndex(tp => tp.id == event.id)
      this.tradedProducts[index] = event
      this.tradedProducts.push()
    },
    newProductAdded(event){
      this.tradedProducts.push(event);
    },
    getNetWeight(item) {
      if (typeof item.net_weight === "number")
        return item.net_weight.toFixed(3);
      return parseFloat(item.net_weight);
    },
    getTotalSellingPrice(item) {
      return item.selling_tonne_price * item.net_weight;
    },
    getTotalBuyingPrice(item) {
      return item.buying_tonne_price * item.net_weight;
    },
    addNewProduct() {
      this.productTradeToEdit = null;
      this.productTradeFormDialog = true;
    },
    editProductTrade(item) {
      this.productTradeToEdit = item;
      this.productTradeFormDialog = true;
    },
    updateChangeRates() {
      this.sendMutation("updated", "change rate", UPDATE_CONTRACT, {
        id: this.contract.id,
        buying_change_rate: parseFloat(this.contract.buying_change_rate),
        selling_change_rate: parseFloat(this.contract.selling_change_rate),
      });
    },
    dragEnd(event) {
      this.sendMutation(
        "no-notif",
        "product trades",
        REORDER_PRODUCT_TRADES,
        { old_index: event.oldIndex + 1, new_index: event.newIndex + 1, contract_id: this.contract.id, provisional: this.previsional }
      )
    }
  },
  mounted() {
    new Sortable(
      this.$refs.sortableTable.$el.getElementsByTagName("tbody")[0],
      {
        draggable: ".sortableRow",
        handle: ".sortHandle",
        animation: 150,
        ghostClass: "sortableRowGhost",
        onEnd: this.dragEnd
      }
    );
  },
};
</script>

<style>
.productTrades table tbody tr td,
.productTrades table thead tr th,
.productTrades table tfoot tr td {
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.productTrades table.v-table tbody td {
  height: 36px !important;
}

.productTrades table.v-table thead tr {
  height: 42px;
}
.sortHandle {
  cursor: move !important;
  cursor: -webkit-grabbing !important;
}

.sortableRow {
  border-bottom: 1px #ddd solid !important;
}

.sortableRowGhost {
  opacity: 0;
}

.reducedInput.v-text-field.v-text-field--solo .v-input__control {
  min-height: 28px;
  padding: 0;
}
</style>


